import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, MapPin, Plus, AlertTriangle } from 'lucide-react';
import CreateTerritoriesModal from './CreateTerritoriesModal';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabase';

interface Territory {
  id: string;
  name: string;
  postal_codes: string[];
}

interface ServiceAreasManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function ServiceAreasManagementModal({
  isOpen,
  onClose
}: ServiceAreasManagementModalProps) {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [territories, setTerritories] = useState<Territory[]>([]);
  const [loading, setLoading] = useState(false);
  const { selectedBusiness } = useBusiness();

  const loadTerritories = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('service_areas')
        .select('*')
        .eq('business_id', selectedBusiness.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setTerritories(data || []);
    } catch (err) {
      console.error('Error loading territories:', err);
    } finally {
      setLoading(false);
    }
  };

  // Calculate overlapping postal codes
  const getOverlappingPostalCodes = (): Set<string> => {
    const postalCodeCounts = new Map<string, number>();
    
    // Count occurrences of each postal code across all territories
    territories.forEach(territory => {
      territory.postal_codes.forEach(code => {
        postalCodeCounts.set(code, (postalCodeCounts.get(code) || 0) + 1);
      });
    });
    
    // Return postal codes that appear more than once
    const overlapping = new Set<string>();
    postalCodeCounts.forEach((count, code) => {
      if (count > 1) {
        overlapping.add(code);
      }
    });
    
    return overlapping;
  };

  // Fix overlapping postal codes by removing duplicates
  const handleFixOverlappingCodes = async () => {
    if (!selectedBusiness?.id || !supabase) return;
    
    const overlappingCodes = getOverlappingPostalCodes();
    if (overlappingCodes.size === 0) return;

    try {
      setLoading(true);
      
      // For each overlapping postal code, keep it in the first territory (by creation date)
      // and remove it from all other territories
      const territoriesSorted = [...territories].sort((a, b) => {
        // Sort by created_at if available, otherwise by name
        const aDate = (a as any).created_at ? new Date((a as any).created_at).getTime() : 0;
        const bDate = (b as any).created_at ? new Date((b as any).created_at).getTime() : 0;
        return aDate - bDate;
      });

      // Process each territory
      for (const territory of territoriesSorted) {
        const updatedCodes = territory.postal_codes.filter(code => {
          // If this code is overlapping, check if we should keep it
          if (overlappingCodes.has(code)) {
            // Keep it only if this is the first territory that has it
            const firstTerritoryWithCode = territoriesSorted.find(t => 
              t.postal_codes.includes(code)
            );
            return firstTerritoryWithCode?.id === territory.id;
          }
          // Keep all non-overlapping codes
          return true;
        });

        // Only update if codes changed
        if (updatedCodes.length !== territory.postal_codes.length) {
          const { error } = await supabase
            .from('service_areas')
            .update({ postal_codes: updatedCodes })
            .eq('id', territory.id);

          if (error) throw error;
        }
      }

      // Reload territories to reflect changes
      await loadTerritories();
    } catch (err) {
      console.error('Error fixing overlapping postal codes:', err);
      alert('Failed to fix overlapping postal codes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadTerritories();
    }
  }, [isOpen, selectedBusiness]);

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* iOS-style backdrop with blur */}
      <div 
        className="fixed inset-0 transition-opacity duration-200"
        style={{
          zIndex: 100080,
          background: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          opacity: 1,
          pointerEvents: 'auto'
        }}
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
      />
      
      {/* Modal - iOS Style; safe area so content sits below notch / above home indicator */}
      <div
        className="fixed inset-0 flex items-center justify-center pointer-events-none sm:items-center"
        style={{
          zIndex: 100090,
          paddingTop: 'env(safe-area-inset-top, 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          paddingLeft: 'env(safe-area-inset-left, 0px)',
          paddingRight: 'env(safe-area-inset-right, 0px)',
        }}
      >
        <div 
          className="bg-white w-full h-full sm:rounded-[20px] sm:shadow-xl sm:max-w-3xl sm:mx-4 sm:max-h-[85vh] flex flex-col pointer-events-auto overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header - iOS Style */}
          <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200/60">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">
              Service Areas
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors touch-manipulation"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-y-auto bg-gray-50/30">
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
              {/* Overlapping Postal Codes Warning */}
              {(() => {
                const overlappingCodes = getOverlappingPostalCodes();
                if (overlappingCodes.size > 0) {
                  return (
                    <div className="bg-red-50 border border-red-200 rounded-[12px] p-3 sm:p-4">
                      <div className="flex items-start">
                        <AlertTriangle className="h-4 w-4 sm:h-5 sm:w-5 text-red-600 mt-0.5 mr-2 flex-shrink-0" />
                        <div className="flex-1">
                          <p className="text-xs sm:text-sm font-medium text-red-900 mb-1">
                            Overlapping Postal Codes Detected
                          </p>
                          <p className="text-xs text-red-700 mb-3">
                            {overlappingCodes.size} postal code{overlappingCodes.size !== 1 ? 's' : ''} appear{overlappingCodes.size === 1 ? 's' : ''} in multiple service areas. These are highlighted in red below.
                          </p>
                          <button
                            onClick={handleFixOverlappingCodes}
                            disabled={loading}
                            className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-[10px] text-white bg-red-600 hover:bg-red-700 active:scale-[0.98] transition-all touch-manipulation shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                            {loading ? (
                              <>
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-2"></div>
                                Fixing...
                              </>
                            ) : (
                              'Fix Overlaps'
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                }
                return null;
              })()}
              
              {/* Service Areas Section */}
              <div>
                <div className="flex items-center justify-between mb-3 sm:mb-4">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-1">Service Areas</h3>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsCreateModalOpen(true);
                    }}
                    className="inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 border border-transparent text-xs sm:text-sm font-medium rounded-[12px] text-white bg-blue-600 hover:bg-blue-700 active:scale-[0.98] transition-all touch-manipulation shadow-sm"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <Plus className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
                    Add Area
                  </button>
                </div>
                
                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                  <div className="p-4 sm:p-6">
                    {loading ? (
                      <div className="flex justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      </div>
                    ) : territories.length === 0 ? (
                      <div className="text-center py-12">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gray-100 flex items-center justify-center">
                          <MapPin className="h-8 w-8 text-gray-400" />
                        </div>
                        <p className="text-sm font-medium text-gray-900 mb-1">No service areas configured</p>
                        <p className="text-xs text-gray-500 mb-4">Create service areas to define where you provide services</p>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsCreateModalOpen(true);
                          }}
                          className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-[12px] hover:bg-blue-100 active:scale-[0.98] transition-all touch-manipulation"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Create your first service area
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {(() => {
                          const overlappingCodes = getOverlappingPostalCodes();
                          return territories.map((territory) => (
                            <div 
                              key={territory.id} 
                              className="border border-gray-200 rounded-[12px] p-4 hover:border-gray-300 transition-colors bg-white"
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1 min-w-0">
                                  <h4 className="text-sm font-semibold text-gray-900 mb-1">{territory.name}</h4>
                                  <p className="text-xs text-gray-500 mb-3">
                                    {territory.postal_codes.length} postal code{territory.postal_codes.length !== 1 ? 's' : ''}
                                  </p>
                                  <div className="flex flex-wrap gap-1.5">
                                    {territory.postal_codes.map((code) => {
                                      const isOverlapping = overlappingCodes.has(code);
                                      return (
                                        <span 
                                          key={code} 
                                          className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${
                                            isOverlapping
                                              ? 'bg-red-50 text-red-700 border-red-200'
                                              : 'bg-gray-100 text-gray-700 border-gray-200'
                                          }`}
                                          title={isOverlapping ? 'This postal code appears in multiple service areas' : ''}
                                        >
                                          {code}
                                        </span>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ));
                        })()}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Create Territories Modal */}
      <CreateTerritoriesModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          setIsCreateModalOpen(false);
          loadTerritories();
        }}
        businessId={selectedBusiness?.id}
      />
    </>,
    document.body
  );
}

